import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CareBridgeAvatar from './CareBridgeAvatar';

/**
 * DraggableAvatarPod Component
 * 180px Floating 3D Avatar Pod with rock-solid mouse & touch drag mobility.
 * - Drags ONLY via dedicated drag handle badge (prevents accidental jumps on mouse movement)
 * - Rendered via ReactDOM.createPortal directly to document.body so transformed container cards never shift its fixed coordinate origin
 * - Tap 3D bubble to trigger friendly wave
 */
const DraggableAvatarPod = ({
  visemeId = 0,
  isSpeaking = false,
  isWaving = false,
  language = 'en',
  onWaveTrigger,
  onStopSpeaking,
}) => {
  // Initial position: Bottom-Right corner default (24px, 24px)
  const [position, setPosition] = useState({ right: 24, bottom: 24 });

  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialRight: position.right,
    initialBottom: position.bottom,
  });

  // Start Dragging strictly on Drag Handle Badge
  const handlePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.button !== undefined && e.button !== 0) return;

    dragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      initialRight: position.right,
      initialBottom: position.bottom,
    };

    setIsDragging(true);
  };

  // Move/up live at window level — guaranteed cleanup no matter where the cursor releases
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      if (!dragRef.current.isDragging) return;

      const deltaX = dragRef.current.startX - e.clientX;
      const deltaY = dragRef.current.startY - e.clientY;

      const podWidth = isMinimized ? 65 : 190;
      const podHeight = isMinimized ? 65 : 230;

      const maxRight = Math.max(10, window.innerWidth - podWidth);
      const maxBottom = Math.max(10, window.innerHeight - podHeight);

      setPosition({
        right: Math.max(10, Math.min(maxRight, dragRef.current.initialRight + deltaX)),
        bottom: Math.max(10, Math.min(maxBottom, dragRef.current.initialBottom + deltaY)),
      });
    };

    const handleUp = () => {
      dragRef.current.isDragging = false;
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    window.addEventListener('blur', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
      window.removeEventListener('blur', handleUp);
    };
  }, [isDragging, isMinimized]);

  // Window Resize Safety (Keeps pod inside screen viewport)
  useEffect(() => {
    const handleResize = () => {
      const podWidth = isMinimized ? 65 : 190;
      const podHeight = isMinimized ? 65 : 230;
      const maxRight = Math.max(10, window.innerWidth - podWidth);
      const maxBottom = Math.max(10, window.innerHeight - podHeight);

      setPosition((prev) => ({
        right: Math.max(10, Math.min(maxRight, prev.right)),
        bottom: Math.max(10, Math.min(maxBottom, prev.bottom)),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMinimized]);

  return ReactDOM.createPortal(
    <div
      className={`draggable-avatar-stage ${isMinimized ? 'is-minimized' : ''} ${isDragging ? 'is-dragging' : ''}`}
      style={{
        right: `${position.right}px`,
        bottom: `${position.bottom}px`,
      }}
    >
      {/* Speaking Status Badge */}
      {isSpeaking && !isMinimized && (
        <div className="draggable-speaking-badge">
          <span className="avatar-status-dot is-speaking"></span>
          <span>{language === 'es' ? 'CareBridge Hablando...' : 'CareBridge Speaking...'}</span>
          {onStopSpeaking && (
            <button
              className="stop-speaking-badge-btn"
              onClick={(e) => {
                e.stopPropagation();
                onStopSpeaking();
              }}
              title={language === 'es' ? 'Detener voz' : 'Stop speaking'}
              aria-label="Stop Speaking"
            >
              🛑 {language === 'es' ? 'Detener' : 'Stop'}
            </button>
          )}
        </div>
      )}

      {/* Prominent Floating Header Controls */}
      <div className="avatar-floating-header">
        {/* Permanent Drag Handle Badge */}
        {!isMinimized && (
          <div
            className="avatar-drag-handle-badge"
            onPointerDown={handlePointerDown}
            title="Click & Drag to reposition"
          >
            ⋮⋮ {language === 'es' ? 'Mover' : 'Drag'}
          </div>
        )}

        {/* High-Visibility Minimize Button Pill */}
        <button
          className="avatar-minimize-pill"
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized((prev) => !prev);
          }}
          title={isMinimized ? 'Expand Avatar' : 'Minimize Avatar'}
          aria-label="Toggle Avatar Size"
        >
          {isMinimized ? '➕' : '➖'}
        </button>
      </div>

      {/* 180px 3D Avatar Bubble (Tapping triggers wave animation) */}
      <div
        className={`draggable-avatar-bubble ${isSpeaking ? 'is-speaking' : ''}`}
        onClick={() => {
          if (onWaveTrigger) onWaveTrigger();
        }}
        title={language === 'es' ? 'Toca para saludar' : 'Tap to wave!'}
      >
        <CareBridgeAvatar
          visemeId={visemeId}
          isSpeaking={isSpeaking}
          isWaving={isWaving}
          width="100%"
          height="100%"
        />
      </div>
    </div>,
    document.body
  );
};

export default DraggableAvatarPod;
